import { Attachment, AttachmentChangedEvent, IActivityAttachment, IAttachment } from "../../../base/common/attachment";
import { IHierarchyNode, NodeFlags } from "./hierarchy";
import { HierarchyNode } from "./hierarchyService";

const inactiveIconName = "icon-inactive";
const activeIconName = "icon-active";

// #region attachment contributors
/**
 * Pluggable per-node attachment contributors (the provider/registry
 * sub-pattern): the TREE PROVIDER runs these at NODE CREATION — the only
 * moment attachments survive, since nodes are recreated freely on project
 * replace / file close. Higher layers (the editor) contribute badges the
 * platform cannot know about (e.g. cloud-asset roots) without the provider
 * importing editor services.
 */
export type TreeAttachmentContributor = (node: IHierarchyNode, object: object) => void;
const treeAttachmentContributors: TreeAttachmentContributor[] = [];

export function registerTreeAttachmentContributor(contributor: TreeAttachmentContributor): { dispose(): void } {
    treeAttachmentContributors.push(contributor);
    return {
        dispose() {
            const index = treeAttachmentContributors.indexOf(contributor);
            if (index >= 0) treeAttachmentContributors.splice(index, 1);
        },
    };
}

export function runTreeAttachmentContributors(node: IHierarchyNode, object: object): void {
    for (const contributor of treeAttachmentContributors) {
        try {
            contributor(node, object);
        }
        catch (error) {
            console.warn("Tree attachment contributor failed", error);
        }
    }
}
// #endregion

export class ActivityAttachment extends Attachment implements IActivityAttachment {

    static getIfExists(object: object) {
        const node = HierarchyNode.get(object);
        if (node) {
            const attachment = node.attachments.find(att => att instanceof ActivityAttachment);
            if (attachment) {
                return attachment as ActivityAttachment;
            }
        }
        return null;
    }

    static notifyForObject(object: object) {
        const node = HierarchyNode.get(object);
        if (node) {
            const attachment = node.attachments.find(att => att instanceof ActivityAttachment);
            if (attachment) {
                attachment.notifyActivity();
            }
        }
    }
    static isActive(object: object) {
        const node = HierarchyNode.get(object);
        if (node) {
            const attachment = node.attachments.find(att => att instanceof ActivityAttachment);
            if (attachment) {
                return attachment.active;
            }
        }
        return false;
    }

    readonly type = 'activity';


    get active() {
        return (Date.now() - this._lastActiveTime) < this._timeoutDuration;
    }
    notifyActivity() {
        this._lastActiveTime = Date.now();
        // called once per render CALL — 18 passes a frame on a post-processing
        // page, times scene/camera/renderer. Already-active is the normal case,
        // and re-asserting it walks the flag maps for nothing.
        if (this._lastActiveState !== true) this._updateState(true);
        // Schedule the inactive check lazily. This can be called per object per
        // frame (e.g. pages animating many objects), so re-arming the timer on
        // every call causes massive clearTimeout/setTimeout churn. Instead keep
        // a single pending timer; when it fires and activity has continued in
        // the meantime it re-arms itself for the remaining time (see _onTimeout).
        if (this._timeoutId === null) {
            this._timeoutId = setTimeout(this._onTimeout, this._timeoutDuration * 1.2);
        }
    }

    constructor(protected readonly node: IHierarchyNode) {
        super('activity', undefined, "", inactiveIconName);
        this._updateState(false);
        // window.requestAnimationFrame(() => this._updateState(false));
    }


    private _lastActiveTime: number = 0;
    private _timeoutId: any = null;
    /**
     * How long after its last render an object still counts as active.
     *
     * This was 50 ms — "rendered within the last frame at 60 fps" — which turns
     * into a flapping loop the moment the page drops below ~16 fps: every frame
     * arrives after the window expired, so the object flips inactive and back,
     * each flip writing NodeFlags.Inactive, firing the node's change event and
     * re-rendering that row in the tree. It only starts once things are slow,
     * and then it keeps them slow (measured: ~52 Svelte flushes a second).
     *
     * Half a second says what the indicator MEANS — "not rendered recently" —
     * and holds down to 2 fps.
     */
    private _timeoutDuration: number = 500;

    private _onTimeout = () => {
        this._timeoutId = null;
        const window = this._timeoutDuration * 1.2;
        const elapsed = Date.now() - this._lastActiveTime;
        if (elapsed < window) {
            // Activity continued since this timer was armed — check again when
            // the current activity window would actually expire.
            this._timeoutId = setTimeout(this._onTimeout, window - elapsed);
            return;
        }
        this._updateState(false);
    }

    private _lastActiveState: boolean | undefined = undefined;

    private _updateState(isActive: boolean) {
        if (isActive !== this._lastActiveState) {
            this._lastActiveState = isActive;
            this._icon = isActive ? activeIconName : inactiveIconName;
            this._description = isActive ? "The object is active!" : "The object is inactive!";
            this._visible = false; // hiding the attachment (we visualize this in another way in the UI, this attachment is only storing the state)
            this._onDidChange.fire({ attachment: this } as AttachmentChangedEvent);
        }

        if (!isActive) HierarchyNode.enableFlag(this.node, NodeFlags.Inactive);
        else HierarchyNode.disableFlag(this.node, NodeFlags.Inactive);
    }

}